import { ColumnState } from 'ag-grid-community'
import { AggregateField } from '../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../lib/functions/workload'
import { SelectedColumnQuickFilterKey } from '../../../view/pages/ProjectPlanNew/projectPlanNew'

// 通常のプロジェクト計画モック画面向けの列設定。
// クイックフィルター「計画」を選択済みの状態で見せるため、列の構成も
// applyColumnQuickFilter（ProjectPlanNew/index.tsx）のPLANNING分岐と
// 同じ並び・固定にする。復元経路はselectedColumnQuickFilterKeyを読んでも
// 列には適用しない（applyColumnQuickFilterはクリック時にしか走らない）ため、
// ボタンの選択表示とのつじつまをここで合わせる必要がある。
//
// 注意: PLANNING分岐の列が変わった場合はここも手動で合わせること。
export const PROJECT_PLAN_MOCK_FUNCTION_UUID = 'mock-project-plan-function-uuid'

// 表示名列（ツリー列＝ag-Grid-AutoColumn）の幅。既定は400px
// （gridOptions/index.tsのautoGroupColumnDef）だが、この画面は右側の列を
// 広く見せたいので狭める。applyColumnStateはwidthも復元するため、列定義を
// 触らずここで指定できる。
const DISPLAY_NAME_COL_ID = 'ag-Grid-AutoColumn'
const DISPLAY_NAME_COL_WIDTH = 300

const PROJECT_PLAN_PINNED_COL_IDS = [
  'drag',
  'rowNumber',
  'body.wbsItem.status',
  // 進捗列は実験設定 wbs-status-column-layout（既定ON）が有効なときだけ
  // 生成され、lockPosition: 'left' でステータス列の直後に並ぶ
  // （defaultColumns.tsのgetProgressRateColumns）。並び順を定義側に合わせる。
  'body.wbsItem.progressRate',
  DISPLAY_NAME_COL_ID,
  'body.wbsItem.team',
  'body.wbsItem.accountable',
  'body.wbsItem.responsible',
  'body.wbsItem.scheduledDate.startDate',
  'body.wbsItem.scheduledDate.endDate',
]

const PROJECT_PLAN_VISIBLE_COL_IDS = [
  'body.wbsItem.estimatedHour.deliverable',
  'body.wbsItem.estimatedHour',
  'ganttChart',
]

// PLANNING分岐はコメント列（action）も固定表示するが、デモでは
// コメント機能を見せないため引き続き隠す。
const PROJECT_PLAN_HIDDEN_COL_IDS = [
  'action', // コメント列（externalId: projectPlan.new.comment）
  // スプリント列のcolIdはFeature Flag（sprints-multi-select）で単複が変わる。
  // 存在しないcolIdは無視されるので両方並べる。
  'body.wbsItem.sprint',
  'body.wbsItem.sprints',
  // 以下はdefaultColumns.tsでhideを持たない＝既定で表示される列。
  // applyColumnStateはColumnStateに無い列を触らないため、ここに挙げないと
  // 表示されたまま残り、applyOrder: trueで上の列が前に詰められる結果、
  // ガントチャートより右に並んでしまう。PLANNING分岐（index.tsx）は
  // 表示リスト外を全てhideするので、それに揃えて明示的に隠す。
  'body.wbsItem.description',
  'body.wbsItem.tags',
  'body.wbsItem.watchers',
  'progress.scheduledProgressRate',
  'progress.progressRate',
  'progress.total',
  'progress.scheduledToBe',
  'progress.completed',
  'progress.preceding',
  'progress.delayed',
  'progress.remaining',
  'evm.costVariance',
  'evm.costPerformanceIndex',
  'evm.scheduleVariance',
  'evm.schedulePerformanceIndex',
  'evm.estimateToComplete',
  'evm.estimateAtCompletion',
  'evm.varianceAtCompletion',
]

export const projectPlanDefaultColumnState: ColumnState[] = [
  ...PROJECT_PLAN_PINNED_COL_IDS.map(colId => ({
    colId,
    hide: false,
    pinned: 'left' as const,
    // 表示名列だけ幅を指定する。他の列は列定義の幅のまま。
    width: colId === DISPLAY_NAME_COL_ID ? DISPLAY_NAME_COL_WIDTH : undefined,
  })),
  ...PROJECT_PLAN_VISIBLE_COL_IDS.map(colId => ({ colId, hide: false })),
  ...PROJECT_PLAN_HIDDEN_COL_IDS.map(colId => ({ colId, hide: true })),
]

// 初期表示で展開しておく行。useBulkSheetState（BulkSheetView/hooks/
// bulkSheetState.ts）が復元し、restoreExpandedRows（ProjectPlanNew/index.tsx）
// がグリッドに適用する。値は行uuidで、デモのWBSはuuid=code（wbs-items.ts）。
//
// デモ画面のgroupDefaultExpandedは1（工程一覧が見える深さ）なので、
// 「要件定義」の配下だけをもう一段開くために、要件定義とその親の
// ワークグループ行を並べる。展開は親から順に効くため、途中の祖先を
// 飛ばすと子が閉じたままになる。
//
// 注意: restoreExpandedRowsは子を持たない行のidを保存値から取り除くため、
// 対象行の配下が無くなると自動で外れる（この一覧の手動メンテは不要）。
const DEMO_WORKGROUP_ROW_ID = 'demo-wg-1'
const REQUIREMENTS_PROCESS_ROW_ID = 'GWIS7KG6' // 工程：要件定義
const REQUIREMENTS_SESSION_ROW_ID = 'YRQL3TCX' // 要件定義セッション

export const projectPlanDefaultExpandedRowIds = [
  DEMO_WORKGROUP_ROW_ID,
  REQUIREMENTS_PROCESS_ROW_ID,
  REQUIREMENTS_SESSION_ROW_ID,
]

// 通常のプロジェクト計画モック画面（ProjectPlan.tsx）の初期表示状態。
// usePageState（ProjectPlanNew/hooks/pageState.ts）は保存値が無ければ
// 集計項目=工数・工数単位=時間・レポート非表示・クイックフィルター未選択で
// 立ち上がるため、デモで見せたい状態をPAGE_STATEの保存値として返す。
// 画面側に初期値のprop/デモ専用分岐を足さず、常設の復元経路だけで完結させる
// 方針は、列設定（projectPlanDefaultColumnState）と同じ。
//
// 注意: usePageStateの復元は値がfalsyなキーを読み飛ばす（`payload.x && setX(x)`）
// ため、reportにfalseを入れても効かない。ここではtrueにしたいので問題にならない。
export const projectPlanDefaultPageState = {
  // 「個数」／「人日」。工数単位は集計項目が個数のときもトグルとして残る。
  aggregateField: AggregateField.WBS_ITEM_COUNT,
  workloadUnit: WorkloadUnit.DAY,
  // クイックフィルター「計画」を選択済みにする。ボタンの選択表示のみで、
  // 列の並び替え自体はapplyColumnQuickFilterがクリック時にしか走らないため、
  // 同じ並びをprojectPlanDefaultColumnStateで再現している。
  selectedColumnQuickFilterKey: SelectedColumnQuickFilterKey.PLANNING,
  // レポートを開いた状態でヘッダーに表示する。
  report: true,
}
